import { Route } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { adminGuard } from './guards/admin.guard';
import { APP_PATHS } from './app.paths';

export const appRoutes: Route[] = [
  {
    path: APP_PATHS.HOME,
    loadComponent: () => import('./pages/home/home').then((m) => m.Home),
    data: {
      title: 'Home',
      description:
        'Watch YouTube videos in sync with your friends on SyncTube.live.',
    },
  },
  {
    path: APP_PATHS.LOGIN,
    loadComponent: () => import('./pages/login/login').then((m) => m.Login),
    data: {
      title: 'Login',
      description:
        'Log in to your SyncTube.live account to manage your playlists.',
    },
  },
  {
    path: APP_PATHS.REGISTER,
    loadComponent: () =>
      import('./pages/register/register').then((m) => m.Register),
    data: {
      title: 'Register',
      description:
        'Create a free SyncTube.live account to start watching videos with friends.',
    },
  },
  {
    path: APP_PATHS.PRIVACY_POLICY,
    loadComponent: () =>
      import('./pages/privacy-policy/privacy-policy.component').then(
        (m) => m.PrivacyPolicyComponent,
      ),
    data: {
      title: 'Privacy Policy',
      description:
        'Learn about how SyncTube.live collects, uses, and protects your personal information.',
    },
  },
  {
    path: APP_PATHS.CONFIRM_EMAIL,
    loadComponent: () =>
      import('./pages/confirm-email/confirm-email.component').then(
        (m) => m.ConfirmEmailComponent,
      ),
    data: {
      title: 'Confirm Email',
      robots: 'noindex, nofollow',
    },
  },
  {
    path: APP_PATHS.FORGOT_PASSWORD,
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent,
      ),
    data: {
      title: 'Forgot Password',
      description: 'Reset your SyncTube.live account password.',
    },
  },
  {
    path: APP_PATHS.RESET_PASSWORD,
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
    data: {
      title: 'Reset Password',
      robots: 'noindex, nofollow',
    },
  },
  {
    path: APP_PATHS.PROFILE,
    loadComponent: () =>
      import('./pages/profile/profile.component').then(
        (m) => m.ProfileComponent,
      ),
    canActivate: [authGuard],
    data: {
      title: 'Profile',
      description: 'Manage your SyncTube.live profile and settings.',
      robots: 'noindex, nofollow',
    },
  },
  {
    path: APP_PATHS.USER_PAGE,
    loadComponent: () =>
      import('./pages/user-page/user-page.component').then(
        (m) => m.UserPageComponent,
      ),
    data: {
      title: 'User Profile',
    },
  },
  {
    path: APP_PATHS.ADMIN,
    loadComponent: () =>
      import('./pages/admin-panel/admin-panel.component').then(
        (m) => m.AdminPanelComponent,
      ),
    canActivate: [adminGuard],
    data: {
      title: 'Admin Panel',
      robots: 'noindex, nofollow',
    },
  },
  {
    path: APP_PATHS.CREATE_PLAYLIST,
    loadComponent: () =>
      import('./pages/profile/create-playlist/create-playlist.component').then(
        (m) => m.CreatePlaylistComponent,
      ),
    canActivate: [authGuard],
    data: {
      title: 'Create Playlist',
      description:
        'Create a new YouTube video playlist to watch in sync with friends.',
    },
  },
  {
    path: 'playlist/:id/edit', // Keep edit as is, using shortId as :id
    loadComponent: () =>
      import('./pages/profile/edit-playlist/edit-playlist.component').then(
        (m) => m.EditPlaylistComponent,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'playlist/:id/:slug',
    loadComponent: () =>
      import('./pages/playlist-page/playlist-page.component').then(
        (m) => m.PlaylistPageComponent,
      ),
  },
  // Redirect old /playlist/:id to use logic inside component or simpler redirect?
  // Actually, wait, if I change the path to :id/:slug, the old URL /playlist/:id will 404.
  // The requirement said: "Legacy UUID URLs are expected to fail or need to be handled by the client (though 404 is acceptable)"
  // But wait, what about /playlist/:shortId without slug?
  // Requirements: "If it doesn't match (e.g., /playlist/AbCd1234/old-slug or /playlist/AbCd1234), redirect to the correct canonical URL."
  // So I should keep a route for `playlist/:id` that redirects to `playlist/:id/slug`?
  // Or handle it in the component.
  // If I define `playlist/:id/:slug`, then `playlist/:id` won't match it.
  // So I should define `playlist/:id` pointing to the SAME component, and let the component handle redirection.
  {
    path: 'playlist/:id',
    loadComponent: () =>
      import('./pages/playlist-page/playlist-page.component').then(
        (m) => m.PlaylistPageComponent,
      ),
  },
];
